<script setup lang="ts">
// When to hide/show the title and player controls:
// - if the video is in the initial state, hide the controls
// - if the video is playing, hide both unless the user hovers over the player
// - otherwise, show both.

import axios from "axios";
import {
  useFullscreen,
  onClickOutside,
  useIdle,
  useWindowSize,
  useMouseInElement,
  usePreferredLanguages,
} from "@vueuse/core";
import {
  computed,
  provide,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  onBeforeMount,
  onUnmounted,
  nextTick,
} from "vue";
import type { Ref } from "vue";
import { calculateRuntime, calculateFormattedRuntime } from "@modules/runtime";
import type { eventWithTime } from "@rrweb/types";
import PureSlide from "@components/PureSlide.vue";
import SpinningCircle from "@components/SpinningCircle.vue";
import Player from "@components/Player.vue";
import PlayerControls from "@components/PlayerControls.vue";
import PlayerDebugger from "@components/PlayerDebugger.vue";
import PlayerQrPanel from "@components/PlayerQrPanel.vue";
import PublicPlayerTimeline from "@components/PublicPlayerTimeline.vue";
import PreloadStyleheetsFromEvents from "@components/PreloadStyleheetsFromEvents.vue";
import Icon from "@components/Icon.vue";
import provideElapsedTime from "@modules/provide_elapsed_time";
import { getErrorMessage } from "@modules/errors";
import { eventHub } from "@modules/event_hub";
import { useActionManipulators } from "@modules/action_manipulators";
import Video, { Language, PublicVideo } from "@models/Video";
import VoiceOver from "@models/VoiceOver";
import { Action, BrowserAction, ImageSlide, TextSlide, VideoSlide } from "@models/ActionHierarchy";
import PublicPlayerAboutScreen from "@components/PublicPlayerAboutScreen.vue";
import AudioRecording from "@models/AudioRecording";
import { type Song } from "@modules/songs";
import type { playerConfig } from "rrweb";
import { triggerSilentAudioPlayback } from "@modules/trigger_silent_audio_playback";
import { useAppsignal } from "@modules/appsignal";
import { beep } from "@modules/beep";

import { useRouter } from "vue-router";

const HEADLESS_STATUS_BAR_HEIGHT = 5;

const router = useRouter();

const eventsApi = axios.create({ baseURL: "/api/v1/events" });

type toggleActionStates = "play" | "pause";

type PropType = {
  videoToken: string;
  language?: string;
  headless?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  replayCanvas?: boolean;
  forcePastInitialState?: boolean; // so that this can be overridden in a story.
  forcePlayerToBeReady?: boolean; // so that this can be overridden in a story.
  forceClapboardOn?: boolean; // so that this can be overridden in a story.
  customRrwebConfig?: Partial<playerConfig>;
};

const props = withDefaults(defineProps<PropType>(), {
  autoplay: false,
  loop: false,
  customRrwebConfig: () => ({}),
});

const isLoading = ref(true);
const isLoadingEvents = ref(true);
const playerIsReady = ref(props.forcePlayerToBeReady ?? false);
const shouldBePlaying = ref<boolean>(props.autoplay);
const isPlaying = ref(false); // Possibly add autoplay param
const isSeeking = ref(0);
const hasStartedPlayer = ref(false); // Only false when the player has never been started
const isFinished = ref(false);
const videoId: Ref<null | number> = ref(null);
const currentActionId: Ref<null | string> = ref(null);
const error: Ref<null | string> = ref(null);
const lastToggleAction: Ref<null | toggleActionStates> = ref(null);
const isFullScreen = ref(false);
const showAboutScreen = ref(false);
const aboutScreen = ref<HTMLDivElement | undefined>(undefined);
const lastToggledPlayAt = ref(0);
const { idle, lastActive } = useIdle(2 * 1000);

const inInitialState = ref(!props.forcePastInitialState);

const events: eventWithTime[] = []; // for performance reasons we don't want the events array to be reactive

function openAboutScreen(): void {
  showAboutScreen.value = true;
  // eslint-disable-next-line no-use-before-define
  pause();
}

onClickOutside(aboutScreen, openAboutScreen);

// computed
const video = computed(() => {
  if (!videoId.value) return null;
  return Video.query()
    .with(["actions", "voiceOvers.audioRecordings"])
    .find(videoId.value) as PublicVideo | null;
});
const audioRecordings = computed(() =>
  AudioRecording.query().where("videoId", videoId.value).orderBy("position").all(),
);
const currentAction = computed(() => {
  if (!currentActionId.value) return null;
  return Action.find(currentActionId.value);
});
const voiceOvers = computed(() => VoiceOver.all());
const showDebugger = computed(() => Boolean(!isLoading.value && video.value && false));

function switchLanguage(code: string): void {
  // We do an old-fashioned redirect here because the player is not reactive yet.
  window.location.replace(
    router.resolve({
      name: "player",
      params: { videoToken: props.videoToken, language: code },
    }).fullPath,
  );
}

const {
  isSupported: fullscreenIsSupported,
  isFullscreen,
  toggle: toggleFullScreen,
} = useFullscreen(document.documentElement);
const playerRoot = ref<HTMLDivElement | null>(null);
const { isOutside: mouseIsOutsidePlayerRoot } = useMouseInElement(playerRoot);

const windowSize = useWindowSize();
const showToggle = computed(() => {
  if (inInitialState.value) return true;
  return false;
});
const showControls = computed(() => {
  if (inInitialState.value) return false;
  if (
    isPlaying.value &&
    (lastToggledPlayAt.value >= lastActive.value || idle.value || mouseIsOutsidePlayerRoot.value)
  )
    return false;
  return true;
});
const showTimeline = computed(
  () => showControls.value && windowSize.width.value > 360 && windowSize.height.value > 200,
);
const showHeader = computed(() => {
  if (inInitialState.value) return true;
  return showControls.value;
});

const lastToggleActionIcon = computed(() => {
  if (lastToggleAction.value) return `${lastToggleAction.value}.fill`;
  if (isPlaying.value) return "play.fill";
  return "pause.fill";
});
const toggleIcon = computed(() => {
  if (lastToggleAction.value) return lastToggleActionIcon.value;
  if (isPlaying.value) return "pause.fill";
  return "play.fill";
});
const nextToggleAction = computed(() => {
  if (!isPlaying.value) return "play.fill";
  return "pause.fill";
});
const visibleActions = computed(() =>
  Action.query()
    .where("videoId", videoId.value)
    .where("visible", true)
    .where("index", (value) => value !== null)
    .orderBy("index")
    .all(),
);

const videoRuntime = computed(() => calculateRuntime(visibleActions.value, voiceOvers.value));
const videoDimensions = computed(() => ({
  width: video.value?.width || 1280,
  height: video.value?.height || 720,
}));

const { setActionPreviewable } = useActionManipulators({
  actions: visibleActions,
  voiceOvers,
  video,
  videoId: videoId.value,
});

const toggleButton: Ref<null | HTMLButtonElement> = ref(null);
function triggerToggleButton(action: "play" | "pause"): void {
  lastToggleAction.value = action;
  // Reset the classname because adding the classname will trigger the CSS animation.
  (toggleButton.value as HTMLButtonElement)?.classList.remove("toggled");
  nextTick(() => {
    // eslint-disable-next-line no-unused-expressions
    (toggleButton.value as HTMLButtonElement)?.offsetWidth; // trigger dom reflow and prime CSS animations
    (toggleButton.value as HTMLButtonElement)?.classList.add("toggled");
    setTimeout(
      () => {
        // prepare the toggle for the next action:
        if (action === "play") lastToggleAction.value = "pause";
        else lastToggleAction.value = "play";
      },
      10 + 1000 + 10,
    ); // the 1s part is tightly coupled with the duration toggle animation (1s).
  });
}

const play = (): void => {
  if (currentAction.value && currentAction.value.isLast) {
    // rewind the player:
    currentActionId.value = video.value?.actions?.[0]?.id || null;
  }
  if (inInitialState.value) {
    triggerToggleButton("play");
  }
  isPlaying.value = true;
  hasStartedPlayer.value = true;
};

const pause = (): void => {
  isPlaying.value = false;
};

watch(isPlaying, (newPlayingState) => {
  if (inInitialState.value) inInitialState.value = false;
  lastToggledPlayAt.value = Date.now();

  if (newPlayingState) navigator.mediaSession.playbackState = "playing";
  else navigator.mediaSession.playbackState = "paused";
});

// respond to media buttons:
navigator.mediaSession?.setActionHandler("play", play);
navigator.mediaSession?.setActionHandler("pause", pause);

onBeforeUnmount(() => {
  // de-register from media buttons:
  navigator.mediaSession?.setActionHandler("play", null);
  navigator.mediaSession?.setActionHandler("pause", null);
});

const isBuffering = computed(() => shouldBePlaying.value && !playerIsReady.value);

const { sendToAppsignal } = useAppsignal();
const togglePlay = (): void => {
  // Until the player is ready, toggling playback will only toggle a shouldBePlaying and .
  // When the player is ready to play, toggling playback will just start the player.
  if (playerIsReady.value) {
    if (isPlaying.value) {
      triggerToggleButton("pause");
      pause();
    } else {
      triggerSilentAudioPlayback(sendToAppsignal);
      triggerToggleButton("play");
      play();
    }
    shouldBePlaying.value = isPlaying.value;
  } else {
    if (shouldBePlaying.value) {
      triggerToggleButton("pause");
    } else {
      triggerSilentAudioPlayback(sendToAppsignal);
      triggerToggleButton("play");
    }
    shouldBePlaying.value = !shouldBePlaying.value;
  }
};
watch(playerIsReady, (isReady) => {
  if (window.playerApp && props.headless) window.playerApp.isReady = isReady;
  if (shouldBePlaying.value) {
    play();
  }
});

const clapboard = ref(false);
watch(clapboard, (val) => {
  if (val) {
    const callback = () => {
      if (!props.forceClapboardOn) clapboard.value = false;
    };
    beep({ duration: 500, callback });
  }
});

const preferredLanguageCodes = usePreferredLanguages();
const preferredAvailableLanguages = computed(
  (): Language[] =>
    preferredLanguageCodes.value
      .map((code) => video.value?.languages?.find((l) => l.code === code))
      .filter(Boolean) as Language[],
);

const currentLanguageCode = computed(() => {
  if (props.language) return props.language;
  return preferredAvailableLanguages.value[0]?.code || video.value?.primaryLanguage || undefined;
});

const translatedLanguageCodeForAPI = computed(() => {
  // If the current language is the primary language of the video, we don't need to send it to the API.
  if (currentLanguageCode.value === video.value?.primaryLanguage) return undefined;
  return currentLanguageCode.value;
});

onBeforeMount(async () => {
  try {
    const vid = (await Video.fetch(
      props.videoToken,
      translatedLanguageCodeForAPI.value,
    )) as PublicVideo;
    if (!vid) throw new Error(`404 - Video not found ${props.videoToken}`);

    // console.log("video", video);
    videoId.value = vid.id;

    currentActionId.value = Action.firstVisibleAction(visibleActions.value)?.id || null;
    isLoading.value = false;

    const eventsResponse = await eventsApi.get("", {
      params: { token: props.videoToken, language: translatedLanguageCodeForAPI.value },
    });
    // this.events is a plain JavaScript variable and not reactive/observable. Therefore we have
    // to manually fire a (global) event so that the player can initialize itself when events are
    // loaded but nothing observable gets changed.
    events.push(...eventsResponse.data);
    eventHub.$emit("loaded-events");
    setActionPreviewable(events);
  } catch (err) {
    error.value = getErrorMessage(err);
    console.error(err);
  }
  isLoading.value = false;
  isLoadingEvents.value = false;
});

onMounted(() => {
  window.playerApp = {
    events,
    play,
    clapboard: () => {
      clapboard.value = true;
    },
    videoRuntime,
    isBuffering,
  };
});

onUnmounted(() => {
  delete window.playerApp;
});

const reachedActionId = (id: string): void => {
  currentActionId.value = id;
};

const jumpToActionId = (actionId: string | null): void => {
  isSeeking.value += 1;
  if (actionId) reachedActionId(actionId);
};

/**
 * Provides `elapsedTime`, `formattedElapsedTime` and `runtimeOfPreviousActions`
 */
provideElapsedTime(isPlaying, currentActionId, visibleActions, voiceOvers);

provide(
  "isPlaying",
  computed(() => isPlaying.value),
);
provide(
  "currentActionId",
  computed(() => currentActionId.value),
);
</script>

<template>
  <PreloadStyleheetsFromEvents :events="events" />
  <div
    ref="playerRoot"
    class="group/player dark relative max-h-screen min-h-screen select-none items-center bg-black text-neutral-100"
    :class="[{ 'is-playing': isPlaying }, !headless && 'flex h-full']"
    :style="headless ? `margin-top: ${HEADLESS_STATUS_BAR_HEIGHT}px;` : ''"
    @click="togglePlay"
    @contextmenu.stop.prevent="
      showAboutScreen = true;
      pause();
    "
  >
    <!-- headless status bar -->
    <div
      v-if="headless"
      :style="`height: ${HEADLESS_STATUS_BAR_HEIGHT}px`"
      class="fixed inset-x-0 top-0 z-[1000] text-neutral-100"
      :class="[
        clapboard && 'bg-[rgb(255,255,0)]',
        !isLoading && isPlaying && 'bg-[rgb(0,255,0)]',
        !isLoading && isFinished && 'bg-[rgb(0,0,255)]',
        error && 'bg-[rgb(255,0,0)]',
      ]"
    />

    <div
      v-if="!headless"
      class="pointer-events-none fixed inset-0 z-40 bg-black/25 transition-opacity"
      :class="[showHeader || showToggle || showControls ? 'opacity-100' : 'opacity-0']"
    >
      <!--
        This semi-transparent, dark background exists separately from the controls, toggle and
        header so it can be put above the player but underneath everything else.
      -->
    </div>

    <header
      v-if="!headless"
      class="fixed top-0 z-50 h-[130px] w-full p-4 text-xs text-white transition-opacity xs:text-base md:text-xl"
      :class="[showHeader ? 'opacity-100' : 'opacity-0']"
      style="text-shadow: 0 0 2px rgba(0, 0, 0, 0.5)"
    >
      <h1 v-if="isLoading"></h1>
      <!-- TODO: replace with screenshot with overlaid title and play button -->
      <template v-else-if="error">
        <h1 class="font-bold">Whoops, the video could not be loaded:</h1>
        <p class="">{{ error }}</p>
      </template>
      <h1 v-else-if="video" class="">
        {{ video.name }}
      </h1>
    </header>
    <div class="z-30 m-auto w-full max-w-full grow overflow-hidden">
      <div class="m-auto aspect-video h-full max-h-screen grow overflow-hidden">
        <template v-if="video && !isLoading">
          <div v-if="!playerIsReady" class="relative aspect-video max-h-screen overflow-hidden">
            <div
              v-if="isBuffering && !headless"
              class="absolute inset-0 z-10 flex place-content-center bg-black/30"
            >
              <SpinningCircle />
            </div>
            <PureSlide
              v-if="currentAction instanceof TextSlide"
              class="h-full w-full"
              v-bind="{
                title: currentAction.title,
                body: currentAction.text,
                // logoUrl: video.logoUrl || '',
                horizontalAlignment: currentAction.horizontalAlignment,
                callToAction: currentAction.callToAction,
                callToActionUrl: currentAction.callToActionUrl,
                textColor: currentAction.textColor,
                backgroundColor: currentAction.backgroundColor,
              }"
            />
            <img
              v-else-if="video?.thumbnailUrl"
              class="w-full object-cover"
              :src="video.thumbnailUrl"
              draggable="false"
            />
            <div v-else class=""></div>
          </div>
          <Player
            v-if="!isLoadingEvents"
            v-show="playerIsReady"
            :video="video"
            :events="events"
            :actions="visibleActions"
            :voice-overs="voiceOvers"
            :audio-recordings="audioRecordings"
            :background-song="video.backgroundSong"
            :current-action-id="currentActionId || undefined"
            :is-playing="isPlaying"
            :is-seeking="isSeeking"
            :is-recording-browser="false"
            :hide-forced-subtitles="!hasStartedPlayer"
            :replay-canvas="replayCanvas"
            :custom-rrweb-config="video.customRrwebPlayerConfig"
            :headless="headless"
            @ready="
              playerIsReady = true;
              props.autoplay ? play() : null;
            "
            @started="isPlaying = true"
            @paused="isPlaying = false"
            @finished="
              isFinished = true;
              props.loop ? play() : null;
            "
            @reached-action-id="
              isSeeking = 0;
              reachedActionId($event);
            "
          />
        </template>
      </div>
    </div>
    <template v-if="!headless && !isLoading && video">
      <div
        class="toggle-overlay pointer-events-none absolute inset-0 z-40 flex cursor-default items-center justify-center drop-shadow-sm transition-colors"
        :class="showToggle ? 'opacity-100' : 'opacity-0'"
      >
        <button
          ref="toggleButton"
          class="flex h-24 w-24 scale-75 items-center justify-center rounded-full p-5 text-white outline-none transition-all group-hover/player:scale-90 group-hover/sm:scale-90 group-hover/player:sm:scale-110 md:scale-100 group-hover/player:md:scale-125"
        >
          <Icon
            :icon="`${toggleIcon}:opaque`"
            class="h-12 w-12 text-white"
            :class="toggleIcon === 'play.fill' ? 'relative left-[3px]' : ''"
          />
        </button>
      </div>
      <a
        v-if="video?.showBranding && !isPlaying"
        class="fixed right-3 z-40 ml-3 flex origin-bottom-right scale-[0.35] flex-col items-center gap-1 rounded-2xl bg-black/5 p-3 pr-5 text-center text-xs leading-none text-white drop-shadow-sm transition-colors hover:bg-black/10 xs:scale-50 md:scale-75"
        :class="showControls ? (showTimeline ? 'bottom-16 sm:bottom-20' : 'bottom-10') : 'bottom-3'"
        href="https://www.recordonce.com/"
        target="_blank"
        @click.stop
      >
        <div class="absolute left-[52px] top-1 hidden xs:block">created with</div>
        <img
          class="block w-48"
          src="@assets/images/Record-Once-logo-405x64px-light.svg"
          alt="Record Once"
        />
      </a>

      <div class="fixed bottom-0 z-50 flex w-full flex-col">
        <div
          class="flex w-full flex-col gap-0 transition-opacity"
          :class="[showControls ? 'opacity-100' : 'opacity-0']"
        >
          <PublicPlayerTimeline
            v-show="showTimeline"
            class="z-30 w-full px-2"
            :actions="visibleActions"
            :voice-overs="voiceOvers"
            :video-runtime="videoRuntime"
            :video-dimensions="videoDimensions"
            :current-action-id="currentActionId || undefined"
            :base-file-url="video.baseFileUrl"
            :logo-url="video.logoUrl || undefined"
            @jump-to-action-id="jumpToActionId($event)"
          />
          <PlayerControls
            v-if="videoId"
            :video-id="videoId"
            :actions="visibleActions"
            :current-action-id="currentActionId || null"
            :voice-overs="voiceOvers"
            :disabled="false"
            :is-playing="isPlaying"
            :allow-full-screen="fullscreenIsSupported"
            :is-full-screen="isFullScreen"
            :video-runtime="videoRuntime"
            :current-language-code="currentLanguageCode"
            :languages="video.languages"
            class="z-30"
            @play="play"
            @pause="pause"
            @jump-to-action-id="jumpToActionId($event)"
            @toggle-full-screen="toggleFullScreen"
            @open-about-screen="openAboutScreen"
            @switch-language="switchLanguage"
          />
        </div>
      </div>
    </template>
    <PublicPlayerAboutScreen
      v-if="showAboutScreen && video?.name && video?.embedHtml && video?.guideUrl"
      ref="aboutScreen"
      :title="video.name"
      :embed-html="video.embedHtml"
      :guide-url="video.guideUrl"
      @close="showAboutScreen = false"
    />

    <PlayerDebugger
      v-if="showDebugger && videoId"
      :is-loading="isLoading"
      :is-playing="isPlaying"
      :video-id="videoId"
      :current-action-id="currentActionId || undefined"
      @play="play"
      @pause="pause"
      @jump-to-action-id="jumpToActionId($event)"
    />
  </div>
  <!-- <PlayerQrPanel
    v-if="headless"
    class="fixed inset-x-0 bottom-0 z-[1000] bg-neutral-800/80 text-neutral-100"
    :is-loading="isLoading"
    :is-playing="isPlaying"
    :is-finished="isFinished"
    :error="error || null"
    :current-action-id="currentActionId || undefined"
  /> -->
</template>

<style lang="scss" scoped>
.toggle-overlay button {
  @keyframes zoomFade {
    0% {
      transform: scale(0.01);
      opacity: 0;
    }

    5% {
      transform: scale(0.5);
      opacity: 1;
    }

    100% {
      transform: scale(2);
      opacity: 0;
    }
  }

  &.toggled {
    animation-duration: 1s;
    animation-timing-function: ease-in-out;
    animation-iteration-count: 1;
    animation-fill-mode: both;
    animation-name: zoomFade;
  }
}
</style>
